import { defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, intoEditable, update } from '@/services/api/resources/entityType';
import { IdProps, sequence, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { versioned } from '@/services/api';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { useEntityTypeForm } from './useEntityTypeForm';
import { useEntityTypeRecord } from './useEntityTypeRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { queryTabName, EntityItemsTabNames } from '@/ui/components/navigation/constants';

export const EntityTypeEditPage = defineComponent<IdProps>({
  name: 'EntityTypeEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('entityTypes')) {
      return renderResourceTeaserPage(t('common.entity-types'));
    }

    if (!hasPermission(Permissions.MANAGE_ENTITY_TYPES)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';

    const record = useEntityTypeRecord();

    const getRecord = useTask(
      () => sequence(get(props.id)),
      ([{ data, version }]) => {
        record.value = intoEditable(data);
        recordVersion = version;
      },
    );
    watch(ref(props.id), getRecord.run);

    const sendRecord = useTask(
      () => update(props.id, versioned(record.value, recordVersion)),
      () => toastAndRedirect(t('entity-type.updated'), 'entityItems', { [queryTabName]: EntityItemsTabNames.TYPES }),
    );

    const { formFields, handleSubmit } = useEntityTypeForm(record, sendRecord);

    return function render() {
      return container(
        [
          pageTitle(t('entity-type.edit')),
          box([loader('form', getRecord, [
            ...formFields(),
            loadingButton(
              t('entity-type.update'),
              handleSubmit,
              sendRecord.state.value.pending,
            ),
          ])]),
        ],
        'narrow',
      );
    };
  },
});
